import { InteractionManagerServerMessage } from "../types";

/**
 * Represents a message to send speech via the interaction manager.
 *
 * @interface SendSpeechMessage
 * @property {string} type - The type of the message, which is "sendSpeech".
 * @property {string} message - The message content to be spoken.
 */
export interface SendSpeechMessage {
  type: "sendSpeech";
  id: string;
  message: string;
  options?: { audioFile?: string };
}

/**
 * Represents a message to send a message the model should respond to via the interaction manager.
 *
 * @interface SendRespondMessage
 * @property {string} type - The type of the message, which is "sendRespond".
 * @property {string} message - The message content the model should respond to.
 */
export interface RespondMessage {
  type: "respond";
  id: string;
  message: string;
  context?: string;
}

/**
 * Manages interactions with the sandai iframe.
 *
 * This class provides methods for making the embedded system say something or stop speaking.
 * It also handles sending and receiving messages between the main window and the iframe.
 *
 * ## Usage
 * ```typescript
 * const iframeElement = document.getElementById("myIframe") as HTMLIFrameElement;
 * const interactionManager = new InteractionManager(iframeElement);
 * interactionManager.say("Hello, world!");
 * interactionManager.stop();
 * ```
 *
 * @class InteractionManager
 *
 * @property {HTMLIFrameElement} _iframe - The iframe element this manager interacts with.
 * @property {URL} _iframeUrl - The URL of the iframe.
 * @property {Array<{ name: string; type: string; label: string }>} _saySchema - The schema for the "say" command.
 * @property {Array<unknown>} _stopSchema - The schema for the "stop" command.
 *
 * @method say - Sends a speech message to the iframe.
 * @method stop - Sends a stop speech command to the iframe.
 * @method _sendMessage - Sends a message to the iframe.
 * @method _listenForMessage - Listens for messages from the iframe.
 * @method _getDocs - Retrieves documentation for available interaction methods.
 */
export interface StopSpeechMessage {
  type: "stopSpeech";
  id: string;
}

/**
 * Manages interactions with the sandai iframe, including sending and stopping speech messages.
 */
export class InteractionManager {
  private _iframe: HTMLIFrameElement;
  private _iframeUrl: URL;

  /**
   * Creates an instance of `InteractionManager`.
   *
   * @param {HTMLIFrameElement} iframe - The sandai iframe element for interaction.
   */
  constructor(iframe: HTMLIFrameElement) {
    this._iframe = iframe;
    this._iframeUrl = new URL(iframe.src);
  }

  private _saySchema = [{ name: "message", type: "string", label: "Message" }];

  /**
   * Sends a message to make the character say something,
   * and waits until speech has started.
   *
   * @param {string} message - The message content to be spoken.
   * @returns {Promise<void>} Resolves when the iframe signals that speech has started.
   */
  say(
    message: string,
    options?: {
      voiceName?: string;
      audioFile?: string;
    },
  ): Promise<void> {
    return new Promise((resolve) => {
      const id = Math.random().toString(36).slice(2);

      const listener = (
        event: MessageEvent<InteractionManagerServerMessage>,
      ) => {
        if (event.origin !== this._iframeUrl.origin) return;
        const data = event.data;
        if (data.type === "speechStarted" && data.id === id) {
          window.removeEventListener("message", listener);
          resolve();
        }
      };

      window.addEventListener("message", listener);

      this._sendMessage({ type: "sendSpeech", message, id, options });
    });
  }

  private _respondSchema = [
    { name: "message", type: "string", label: "Message" },
    { name: "context", type: "string", label: "Context" },
  ];

  /**
   * Sends a message to make the character respond using the built-in LLM,
   * and waits for a response.
   *
   * @param {string} message - The message content the model responds to.
   * @param {string} [context] - Optional context for the model.
   * @returns {Promise<string>} Resolves with the model's response.
   */
  respond(message: string, context?: string): Promise<string> {
    return new Promise((resolve) => {
      const id = Math.random().toString(36).slice(2);

      const listener = (
        event: MessageEvent<InteractionManagerServerMessage>,
      ) => {
        if (event.origin !== this._iframeUrl.origin) return;
        const data = event.data;
        if (data.type === "characterResponded" && data.id === id) {
          window.removeEventListener("message", listener);
          resolve(data.message);
        }
      };

      window.addEventListener("message", listener);

      this._sendMessage({
        type: "respond",
        id,
        message,
        context,
      });
    });
  }

  private _stopSchema = [];

  /**
   * Sends a message to make the character stop speaking,
   * and waits until speech has actually stopped.
   *
   * @returns {Promise<void>} Resolves when the iframe signals that speech has stopped.
   */
  stop(): Promise<void> {
    return new Promise((resolve) => {
      const id = Math.random().toString(36).slice(2);

      const listener = (
        event: MessageEvent<InteractionManagerServerMessage>,
      ) => {
        if (event.origin !== this._iframeUrl.origin) return;
        const data = event.data;
        if (data.type === "speechStopped" && data.id === id) {
          window.removeEventListener("message", listener);
          resolve();
        }
      };

      window.addEventListener("message", listener);

      this._sendMessage({ type: "stopSpeech", id });
    });
  }

  /**
   * Sends a message to the iframe's content window.
   *
   * @private
   * @param {SendSpeechMessage | StopSpeechMessage | RespondMessage} data - The message data to send.
   * @throws {Error} Throws an error if the iframe's `contentWindow` is not available.
   */
  _sendMessage(data: SendSpeechMessage | StopSpeechMessage | RespondMessage) {
    if (this._iframe.contentWindow) {
      this._iframe.contentWindow.postMessage(data, "*");
    } else {
      throw new Error("iframe doesn't have a 'contentWindow' member");
    }
  }

  /**
   * Stores all registered message event listeners for later removal.
   *
   * @private
   */
  private _listeners: Array<
    (event: MessageEvent<InteractionManagerServerMessage>) => void
  > = [];

  /**
   * Listens for messages from the iframe and invokes the provided callback when a message is received.
   * Only messages from the iframe's origin are processed.
   *
   * The listener is stored internally so it can be removed later via `destroy()`.
   *
   * @private
   * @param {(data: InteractionManagerServerMessage) => void} callback - The callback function to invoke with the message data.
   */
  private _listenForMessage(
    callback: (data: InteractionManagerServerMessage) => void,
  ) {
    const listener = (event: MessageEvent<InteractionManagerServerMessage>) => {
      if (event.origin !== this._iframeUrl.origin) return;
      callback(event.data);
    };
    this._listeners.push(listener);
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
      this._listeners = this._listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Cleans up all registered message event listeners previously added by `_listenForMessage`.
   * Call this method when the instance is being destroyed or no longer needs to listen to messages.
   */
  destroy() {
    this._listeners.forEach((listener) => {
      window.removeEventListener("message", listener);
    });
    this._listeners = [];
  }

  /**
   * Generates documentation for the `InteractionManager` class, including schemas and methods.
   *
   * @private
   * @returns {Map<string, Map<string, {schema: any[], func: Function}>>} A map containing documentation for the `say` and `stop` methods.
   */
  _getDocs() {
    // Create the docs
    const sayDocs = new Map<string, { schema: any[]; func: Function }>();
    sayDocs.set("Make the character say something", {
      schema: this._saySchema,
      func: this.say.bind(this),
    });

    const stopDocs = new Map<string, { schema: any[]; func: Function }>();
    stopDocs.set("Make the character stop speaking", {
      schema: this._stopSchema,
      func: this.stop.bind(this),
    });

    const respondDocs = new Map<string, { schema: any[]; func: Function }>();
    respondDocs.set("Make the character respond", {
      schema: this._respondSchema,
      func: this.respond.bind(this),
    });

    const interactionManagerDocs = new Map<
      string,
      typeof sayDocs | typeof stopDocs | typeof respondDocs
    >();
    interactionManagerDocs.set("say(message: string)", sayDocs);
    interactionManagerDocs.set("stop()", stopDocs);
    interactionManagerDocs.set(
      "respond(message: string, context: string)",
      respondDocs,
    );

    return interactionManagerDocs;
  }
}
